import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserCheck, ArrowLeft, Mail, Lock, User, Building2,
  CheckCircle, AlertCircle
} from '../components/icons';
import { supabase } from '../lib/supabase';

export function CoachRegistrationPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    partnerCode: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      let partnerId: string | null = null;

      if (formData.partnerCode) {
        const { data: partner } = await supabase
          .from('partners')
          .select('id')
          .eq('id', formData.partnerCode)
          .maybeSingle();

        if (!partner) {
          setError('Invalid partner code');
          setIsLoading(false);
          return;
        }

        partnerId = partner.id;
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (signUpError || !authData.user) {
        throw signUpError || new Error('Failed to create account');
      }

      await supabase.from('users').insert({
        id: authData.user.id,
        email: formData.email,
        full_name: formData.name,
        role: 'coach',
      });

      await supabase.from('coaches').insert({
        user_id: authData.user.id,
        partner_id: partnerId,
        tier: 'basic',
        max_respondents: 25,
        assessment_code: crypto.randomUUID().slice(0, 8).toUpperCase(),
      });

      navigate('/coach');
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err?.message?.includes('already registered')) {
        setError('An account with this email already exists');
      } else {
        setError(err?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    'Manage and track your respondents',
    'Access assessment results and insights',
    'Generate AI-powered analyses (Advanced tier)',
    'Expand your coaching practice'
  ];

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gradient-to-br from-sage-600 to-sage-700 rounded-2xl p-8 text-white"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-white/20 rounded-xl">
                <UserCheck className="w-6 h-6" />
              </div>
              <h1 className="freud-heading-xl text-white">Become a Coach</h1>
            </div>

            <p className="freud-text-base text-sage-100 mb-8">
              Join our platform and help your clients unlock their full potential with our comprehensive assessment tools.
            </p>

            <h3 className="freud-label text-white mb-4">Benefits include:</h3>
            <ul className="space-y-3">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-300" />
                  <span className="freud-text-sm text-sage-50">{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-4 bg-white/10 rounded-xl">
              <h4 className="freud-label text-white mb-2">Subscription Tiers</h4>
              <div className="space-y-2 freud-text-sm text-sage-100">
                <p><strong>Basic:</strong> Up to 25 respondents</p>
                <p><strong>Basic Plus:</strong> Up to 50 respondents</p>
                <p><strong>Advanced:</strong> Unlimited + AI Analysis</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="freud-card-elevated p-8"
          >
            <h2 className="freud-heading-lg text-chocolate-900 mb-6">Create Your Account</h2>

            {error && (
              <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="freud-text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="freud-label block text-chocolate-800 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="freud-input pl-10"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label className="freud-label block text-chocolate-800 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="freud-input pl-10"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="freud-label block text-chocolate-800 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    className="freud-input pl-10"
                    placeholder="Minimum 8 characters"
                  />
                </div>
              </div>

              <div>
                <label className="freud-label block text-chocolate-800 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="freud-input pl-10"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              <div>
                <label className="freud-label block text-chocolate-800 mb-2">
                  Partner Code (Optional)
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                  <input
                    type="text"
                    value={formData.partnerCode}
                    onChange={(e) => setFormData({ ...formData, partnerCode: e.target.value })}
                    className="freud-input pl-10"
                    placeholder="Enter if you have a partner code"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="freud-btn-primary w-full"
              >
                {isLoading ? 'Creating Account...' : 'Create Coach Account'}
              </button>
            </form>

            <p className="mt-6 text-center freud-text-sm text-chocolate-500">
              Already have an account?{' '}
              <Link to="/login" className="text-coral-600 hover:text-coral-700 font-medium">
                Sign in
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
